import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useUnsavedChangesGuard from '../../../hooks/useUnsavedChangesGuard';
import ConfirmModal from '../../utils/ConfirmModal';
import notify from '../../../utils/notify';
import { exportRowsToExcel } from '../../../utils/excelExport';
import { uploadImageFile } from '../../../utils/imageUpload';
import { studioImportApi, studioLabel } from './adminImportApi';
import { clearDraft, storeDraft } from './importDrafts';
import {
  assignColumnField,
  autoMapColumns,
  buildProductPayload,
  buildReviewRows,
  catalogKey,
  catalogKeySet,
  defaultSellPriceMode,
  effectiveSellPriceMode,
  fillMissingTradePrices,
  getImportFields,
  mapWithConcurrency,
  matchImageFiles,
  sellPriceModes,
  summarizeRows,
} from './productImport';
import { ImportHeader, ImportStepper, ImportTarget } from './ImportFrame';
import ImportMapStep from './ImportMapStep';
import ImportReviewStep from './ImportReviewStep';
import ImportResultStep from './ImportResultStep';
import ImportProgress, { createProgress } from './ImportProgress';
import '../../../styles/ProductImport.css';

const EMPTY_STATS = {
  combinedRows: 0,
  missingName: 0,
  alreadyInCatalog: 0,
  archived: 0,
  nonProduct: 0,
  expiredPhotos: 0,
};
const ALREADY_IN_CATALOG = 'Already in Products & Services';

// A photo the file links to is copied in, unless one was uploaded during the
// review instead.
const needsPhotoCopy = (row) =>
  Boolean(row.imageLink) && (!row.imageUrl || row.imageUrl === row.imageLink);

const STATUS_LABELS = {
  imported: 'Imported',
  failed: 'Failed',
  skipped: 'Skipped',
  removed: 'Removed (import undone)',
};

// Maps, reviews and saves a sheet of products or services into the studio a
// support admin picked. DataImportWizard has already read the file (or found a
// saved draft) and loaded the studio's catalog.
export default function ProductImportWizard({
  account,
  onChangeAccount,
  catalog,
  itemType,
  fileName,
  sheet: freshSheet,
  draft,
  onBackToUpload,
  onImportAnother,
  onImportNextSheet,
  nextSheetLabel,
  onFinished,
}) {
  const studioId = account.userId;
  const studioName = studioLabel(account);
  const api = useMemo(() => studioImportApi(studioId), [studioId]);
  const sheet = draft ? draft.sheet : freshSheet;

  const [step, setStep] = useState(draft ? 3 : 2);
  const [mapping, setMapping] = useState(() =>
    draft ? draft.mapping || [] : autoMapColumns(sheet.headers, sheet.dataRows, itemType)
  );
  const [sellPriceMode, setSellPriceMode] = useState(() =>
    draft
      ? draft.sellPriceMode || 'file'
      : defaultSellPriceMode(autoMapColumns(sheet.headers, sheet.dataRows, itemType))
  );
  const [rows, setRows] = useState(() => (draft ? draft.rows : []));
  const [rowStats, setRowStats] = useState(() =>
    draft ? draft.rowStats || EMPTY_STATS : EMPTY_STATS
  );
  const [rowsKey, setRowsKey] = useState(() => (draft ? draft.rowsKey || '' : ''));
  const [uploadingRowIds, setUploadingRowIds] = useState(() => new Set());
  const [importing, setImporting] = useState(null);
  const [progress, setProgress] = useState(null);
  const [results, setResults] = useState([]);
  const [attachingIds, setAttachingIds] = useState(() => new Set());
  const [confirmUndo, setConfirmUndo] = useState(false);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const {
    reload: refreshCatalog,
    products: catalogItems,
    vendors,
    categoryOptions,
    salesCodeOptions,
    addVendor,
    addCategory,
  } = catalog;

  const fields = useMemo(() => getImportFields(itemType), [itemType]);
  const sellMode = effectiveSellPriceMode(mapping, sellPriceMode);
  const isService = itemType === 'service';
  const noun = isService ? 'service' : 'product';
  const nouns = isService ? 'services' : 'products';
  const Nouns = isService ? 'Services' : 'Products';

  /* ----- Leaving mid-import ----- */
  // A fresh file has unsaved choices from the start; a resumed draft doesn't
  // until it's changed.
  const dirtyRef = useRef(!draft);
  const stepRef = useRef(step);
  stepRef.current = step;
  const importingRef = useRef(false);
  const hasUnsavedChanges = useCallback(
    () => importingRef.current || (dirtyRef.current && [2, 3].includes(stepRef.current)),
    []
  );
  const unsavedGuard = useUnsavedChangesGuard(hasUnsavedChanges);

  const changeStudio = () => {
    if (hasUnsavedChanges()) setConfirmSwitch(true);
    else onChangeAccount();
  };

  const markBusy = (setter, ids, busy) =>
    setter((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (busy ? next.add(id) : next.delete(id)));
      return next;
    });

  const vendorNameFor = (choice) =>
    choice.startsWith('new:')
      ? choice.slice(4)
      : vendors.find((v) => v.vendorId === choice)?.name || '';

  /* ----- Drafts ----- */
  const saveDraft = () => {
    try {
      storeDraft(studioId, {
        fileName,
        itemType,
        sheet,
        mapping,
        sellPriceMode,
        rows,
        rowStats,
        rowsKey,
      });
      dirtyRef.current = false;
      notify.success(`Draft saved in this browser. Pick ${studioName} again to finish it later.`);
    } catch (err) {
      console.error('Failed to save import draft', err);
      notify.error("Couldn't save a draft in this browser. Finish the import now instead.");
    }
  };

  /* ----- Step 2: mapping ----- */
  const continueToReview = () => {
    const key = JSON.stringify([itemType, sheet?.name, mapping, sellMode]);
    if (key !== rowsKey) {
      const built = buildReviewRows({
        dataRows: sheet.dataRows,
        mapping,
        headers: sheet.headers,
        itemType,
        sellPriceMode: sellMode,
        vendors,
        categoryNames: [...categoryOptions.custom, ...categoryOptions.preset],
        salesCodeOptions,
        existingProducts: catalogItems,
      });
      setRows(built.rows);
      setRowStats(built.stats);
      setRowsKey(key);
    }
    dirtyRef.current = true;
    setStep(3);
  };

  /* ----- Step 3: review ----- */
  const updateRow = useCallback((id, patch) => {
    dirtyRef.current = true;
    setRows((prev) =>
      prev.map((row) =>
        row.id !== id
          ? row
          : {
              ...row,
              ...patch,
              fields: patch.fields ? { ...row.fields, ...patch.fields } : row.fields,
            }
      )
    );
  }, []);

  const selectRows = useCallback((ids, selected) => {
    dirtyRef.current = true;
    const chosen = new Set(ids);
    setRows((prev) =>
      prev.map((row) =>
        chosen.has(row.id) && row.selected !== selected ? { ...row, selected } : row
      )
    );
  }, []);

  // Bulk edits from the review step. `key` is a field, or 'vendor' for the
  // vendor choice. Rows that already have the value keep their identity.
  const applyToRows = useCallback((ids, key, value) => {
    dirtyRef.current = true;
    const chosen = new Set(ids);
    setRows((prev) =>
      prev.map((row) => {
        if (!chosen.has(row.id)) return row;
        if (key === 'vendor') {
          return row.vendorChoice === value ? row : { ...row, vendorChoice: value };
        }
        return row.fields[key] === value
          ? row
          : { ...row, fields: { ...row.fields, [key]: value } };
      })
    );
  }, []);

  const fillTradePrices = () => {
    const { rows: next, filled } = fillMissingTradePrices(rows);
    if (!filled) return;
    dirtyRef.current = true;
    setRows(next);
    notify.success(
      `Filled ${filled} trade ${filled === 1 ? 'price' : 'prices'} with the price before markup. Check them before importing.`
    );
  };

  // Joined into a string first so the list keeps its identity while the names
  // don't change, which lets unchanged review rows skip re-rendering.
  const categoryChoicesKey = useMemo(() => {
    const byKey = new Map();
    [
      ...categoryOptions.custom,
      ...categoryOptions.preset,
      ...rows.map((r) => r.fields.category),
    ].forEach((name) => {
      const trimmed = String(name || '').trim();
      if (trimmed && !byKey.has(trimmed.toLowerCase())) byKey.set(trimmed.toLowerCase(), trimmed);
    });
    return [...byKey.values()].sort((a, b) => a.localeCompare(b)).join('\n');
  }, [categoryOptions, rows]);
  const categoryChoices = useMemo(
    () => (categoryChoicesKey ? categoryChoicesKey.split('\n') : []),
    [categoryChoicesKey]
  );

  const uploadRowImage = async (rowId, file) => {
    markBusy(setUploadingRowIds, [rowId], true);
    try {
      const url = await uploadImageFile(file);
      updateRow(rowId, { imageUrl: url });
    } catch (err) {
      console.error('Failed to upload import image', err);
      notify.error(err?.message || 'Image upload failed.');
    } finally {
      markBusy(setUploadingRowIds, [rowId], false);
    }
  };

  const uploadMatchingImages = async (files) => {
    const { matches, unmatched } = matchImageFiles(rows, files);
    if (!matches.length) {
      notify.warning(`None of these files match an image filename in ${fileName}.`);
      return;
    }
    const allIds = matches.flatMap((m) => m.rowIds);
    markBusy(setUploadingRowIds, allIds, true);
    let photosAdded = 0;
    let failedUploads = 0;
    await mapWithConcurrency(matches, 3, async ({ file, rowIds }) => {
      try {
        const url = await uploadImageFile(file);
        dirtyRef.current = true;
        setRows((prev) =>
          prev.map((row) => (rowIds.includes(row.id) ? { ...row, imageUrl: url } : row))
        );
        photosAdded += rowIds.length;
      } catch (err) {
        console.error('Failed to upload import image', err);
        failedUploads += 1;
      } finally {
        markBusy(setUploadingRowIds, rowIds, false);
      }
    });
    const parts = [`Added photos to ${photosAdded} ${photosAdded === 1 ? noun : nouns}.`];
    if (unmatched.length) {
      parts.push(
        `${unmatched.length} ${unmatched.length === 1 ? "file didn't" : "files didn't"} match any filename.`
      );
    }
    if (failedUploads) {
      parts.push(`${failedUploads} ${failedUploads === 1 ? 'upload' : 'uploads'} failed.`);
    }
    (failedUploads ? notify.warning : notify.success)(parts.join(' '));
  };

  const handleAddVendor = async (name, assignRowId) => {
    const created = await addVendor(name);
    if (!created?.vendorId) {
      notify.error(`Couldn't add ${name}. Try again.`);
      return null;
    }
    const savedName = created.name || name;
    const fromFile = `new:${savedName}`.toLowerCase();
    dirtyRef.current = true;
    // Rows waiting to create this vendor now use the saved one.
    setRows((prev) =>
      prev.map((row) => {
        if (row.vendorChoice.toLowerCase() === fromFile)
          return { ...row, vendorChoice: created.vendorId };
        if (assignRowId && row.id === assignRowId && !row.vendorChoice) {
          return { ...row, vendorChoice: created.vendorId };
        }
        return row;
      })
    );
    if (created.__duplicate) {
      notify.warning(`${savedName} already exists, so the existing vendor is used.`);
    } else {
      notify.success(`${savedName} added.`);
    }
    return created;
  };

  /* ----- Saving ----- */
  // Saves rows and returns one outcome per row. Rows that turned up in the
  // catalog since the review was built (an earlier run, the studio adding them,
  // a retry after a save that did go through) are skipped, so running it again
  // never makes duplicates. Rows re-ticked despite the warning still save.
  // Each step reports to the progress window as it goes.
  const importRows = async (rowsToImport) => {
    const progress = createProgress(setProgress, `Importing ${nouns} into ${studioName}`, [
      { key: 'check', label: `Checking what ${studioName} already has` },
      { key: 'categories', label: 'Adding new categories' },
      { key: 'vendors', label: 'Adding new vendors' },
      { key: 'photos', label: 'Copying photos from the file' },
      { key: 'save', label: `Saving ${nouns}` },
    ]);
    const outcomeBase = (row, payload, vendorName) => ({
      id: row.id,
      sourceRows: row.sourceRows,
      name: payload.name,
      vendor: vendorName,
      category: payload.category,
      payload,
    });

    progress.start('check');
    const fresh = await refreshCatalog();
    const existingKeys = fresh ? catalogKeySet(fresh.products || []) : null;
    const skipped = [];
    const toSave = [];
    rowsToImport.forEach((row) => {
      const vendorName = vendorNameFor(row.vendorChoice);
      if (!row.alreadyInCatalog && existingKeys?.has(catalogKey(row.fields.name, vendorName))) {
        const payload = buildProductPayload(row, { itemType, userId: studioId, vendorName });
        skipped.push({
          ...outcomeBase(row, payload, vendorName),
          status: 'skipped',
          error: ALREADY_IN_CATALOG,
        });
      } else {
        toSave.push(row);
      }
    });

    // Everything the other steps will do is worked out now, so each step's size
    // is known from the start and the bar only moves forward.
    const studioCategories = new Set(
      (fresh?.categories || []).map((c) =>
        String(c.name || '')
          .trim()
          .toLowerCase()
      )
    );
    const newCategories = [];
    toSave.forEach((row) => {
      const name = row.fields.category.trim();
      if (!name || studioCategories.has(name.toLowerCase())) return;
      studioCategories.add(name.toLowerCase());
      newCategories.push(name);
    });
    const vendorChoices = [...new Set(toSave.map((r) => r.vendorChoice).filter(Boolean))];
    const newVendorChoices = vendorChoices.filter((choice) => choice.startsWith('new:'));
    // Rows can share a photo, so each link is copied once.
    const photoLinks = [...new Set(toSave.filter(needsPhotoCopy).map((r) => r.imageLink))];
    progress.finish('check');
    progress.plan('categories', newCategories.length);
    progress.plan('vendors', newVendorChoices.length);
    progress.plan('photos', photoLinks.length);
    progress.plan('save', toSave.length);

    progress.start('categories');
    await mapWithConcurrency(newCategories, 4, async (name) => {
      await addCategory(name);
      progress.tick('categories');
    });
    progress.finish('categories');

    progress.start('vendors');
    const vendorByChoice = new Map(
      vendorChoices
        .filter((choice) => !choice.startsWith('new:'))
        .map((choice) => [choice, { vendorId: choice, name: vendorNameFor(choice) }])
    );
    const failedVendors = [];
    await mapWithConcurrency(newVendorChoices, 4, async (choice) => {
      const name = choice.slice(4);
      const created = await addVendor(name);
      if (!created?.vendorId) failedVendors.push(name);
      vendorByChoice.set(choice, {
        vendorId: created?.vendorId || '',
        name: created?.name || name,
      });
      progress.tick('vendors');
    });
    progress.finish('vendors');

    // Export links stop working within a day or so, so the photos are copied
    // into the studio's uploads. A product whose photo can't be copied still
    // saves, without it.
    progress.start('photos');
    const copiedPhotos = new Map();
    const photoErrors = new Map();
    await mapWithConcurrency(photoLinks, 4, async (link) => {
      try {
        const { fileUrl } = await api.copyImage(link);
        copiedPhotos.set(link, fileUrl);
      } catch (err) {
        console.error('Failed to copy a photo', err);
        photoErrors.set(link, err?.message || "Couldn't copy the photo");
      } finally {
        progress.tick('photos');
      }
    });
    progress.finish('photos');

    let done = skipped.length;
    setImporting({ done, total: rowsToImport.length });
    progress.start('save');
    const saved = await mapWithConcurrency(toSave, 4, async (row) => {
      const vendor = vendorByChoice.get(row.vendorChoice) || { vendorId: '', name: '' };
      const copied = needsPhotoCopy(row) ? copiedPhotos.get(row.imageLink) : '';
      const payload = buildProductPayload(copied ? { ...row, imageUrl: copied } : row, {
        itemType,
        userId: studioId,
        vendorId: vendor.vendorId,
        vendorName: vendor.name,
      });
      const base = {
        ...outcomeBase(row, payload, vendor.name),
        photoError:
          needsPhotoCopy(row) && !payload.imageUrl
            ? photoErrors.get(row.imageLink) || "Couldn't copy the photo"
            : '',
      };
      try {
        const { itemId } = await api.createProduct(payload);
        return { ...base, status: 'imported', itemId };
      } catch (err) {
        console.error('Failed to import row', row.sourceRows, err);
        return { ...base, status: 'failed', error: err?.message || `Failed to save ${noun}` };
      } finally {
        done += 1;
        setImporting({ done, total: rowsToImport.length });
        progress.tick('save');
      }
    });
    progress.finish('save');

    if (failedVendors.length) {
      notify.warning(
        `Couldn't add ${failedVendors.join(', ')} as ${
          failedVendors.length === 1 ? 'a vendor' : 'vendors'
        }. Their ${nouns} were saved with the vendor name only.`
      );
    }

    // Back in the order of the review table.
    const byId = new Map([...skipped, ...saved].map((outcome) => [outcome.id, outcome]));
    return rowsToImport.map((row) => byId.get(row.id));
  };

  const reportFinished = (outcomes) =>
    onFinished?.(outcomes.filter((r) => r.status === 'imported').length);

  const runImport = async () => {
    if (importingRef.current) return;
    const selected = rows.filter((row) => row.selected);
    if (!selected.length || summarizeRows(rows, itemType).blocking > 0) return;

    importingRef.current = true;
    setImporting({ done: 0, total: selected.length });
    try {
      const outcomes = await importRows(selected);
      setResults(outcomes);
      reportFinished(outcomes);
      clearDraft(studioId);
      dirtyRef.current = false;
      setStep(4);
      refreshCatalog();
    } catch (err) {
      console.error('Import failed', err);
      notify.error(err?.message || 'The import stopped before finishing. Try again.');
    } finally {
      importingRef.current = false;
      setImporting(null);
      setProgress(null);
    }
  };

  /* ----- Step 4: results ----- */
  const retryFailed = async () => {
    if (importingRef.current) return;
    const failedIds = new Set(results.filter((r) => r.status === 'failed').map((r) => r.id));
    const toRetry = rows.filter((row) => failedIds.has(row.id));
    if (!toRetry.length) return;

    importingRef.current = true;
    setImporting({ done: 0, total: toRetry.length });
    try {
      const outcomes = await importRows(toRetry);
      const byId = new Map(outcomes.map((outcome) => [outcome.id, outcome]));
      const next = results.map((r) => byId.get(r.id) || r);
      setResults(next);
      reportFinished(next);
      refreshCatalog();
      const stillFailing = outcomes.filter((o) => o.status === 'failed').length;
      if (stillFailing) {
        notify.warning(`${stillFailing} still couldn't be saved. The report has the reasons.`);
      } else {
        notify.success(`All ${outcomes.length} retried ${nouns} were imported.`);
      }
    } catch (err) {
      console.error('Retry failed', err);
      notify.error(err?.message || 'The retry stopped before finishing. Try again.');
    } finally {
      importingRef.current = false;
      setImporting(null);
      setProgress(null);
    }
  };

  const undoImport = async () => {
    setConfirmUndo(false);
    const created = results.filter((r) => r.status === 'imported' && r.itemId);
    if (!created.length || importingRef.current) return;
    importingRef.current = true;
    setUndoing(true);
    try {
      const deleted = await mapWithConcurrency(created, 4, async (result) => {
        try {
          await api.deleteProduct(result.itemId);
          return result.id;
        } catch (err) {
          // 404 means it's already gone, which is what undo wants.
          if (err.status === 404) return result.id;
          console.error('Failed to delete imported item', result.itemId, err);
          return null;
        }
      });
      const removed = new Set(deleted.filter(Boolean));
      const next = results.map((r) =>
        removed.has(r.id) ? { ...r, status: 'removed', error: '' } : r
      );
      setResults(next);
      reportFinished(next);
      refreshCatalog();
      if (removed.size === created.length) {
        notify.success(`Deleted the ${removed.size} ${nouns} this import created.`);
      } else {
        notify.warning(
          `Deleted ${removed.size} of ${created.length}. Try Undo again for the rest.`
        );
      }
    } finally {
      importingRef.current = false;
      setUndoing(false);
    }
  };

  const attachResultImage = async (resultId, file) => {
    const result = results.find((r) => r.id === resultId);
    if (!result?.itemId) return;
    markBusy(setAttachingIds, [resultId], true);
    try {
      const url = await uploadImageFile(file);
      const payload = { ...result.payload, imageUrl: url };
      await api.updateProduct(result.itemId, payload);
      setResults((prev) => prev.map((r) => (r.id === resultId ? { ...r, payload } : r)));
    } catch (err) {
      console.error('Failed to attach image', err);
      notify.error(err?.message || 'Failed to attach the image.');
    } finally {
      markBusy(setAttachingIds, [resultId], false);
    }
  };

  const downloadReport = () => {
    const base = fileName.replace(/\.[^.]+$/, '') || 'import';
    exportRowsToExcel({
      filename: `${base} - import report.xlsx`,
      sheetName: 'Import report',
      headers: [
        'Excel rows',
        isService ? 'Service' : 'Product',
        'Vendor',
        'Category',
        'Trade price',
        'Selling price',
        'Status',
        'Problem',
        'Image',
        'InteriorFolio ID',
      ],
      rows: results.map((r) => [
        r.sourceRows.join(', '),
        r.name,
        r.vendor,
        r.category,
        r.payload.price,
        r.payload.sellingPrice ?? '',
        STATUS_LABELS[r.status] || r.status,
        r.error || (r.photoError ? `Photo not copied: ${r.photoError}` : ''),
        r.payload.imageUrl ? 'Attached' : 'Not attached',
        r.status === 'imported' ? r.itemId : '',
      ]),
    });
  };

  /* ----- Layout ----- */
  const importedCount = results.filter((r) => r.status === 'imported').length;
  const resultHeading = () => {
    if (importedCount === 0 && results.some((r) => r.status === 'removed')) {
      return ['Import undone', `The ${nouns} from this import were deleted from ${studioName}.`];
    }
    if (results.some((r) => r.status === 'failed')) {
      return ['Import complete', `Some ${nouns} couldn't be imported into ${studioName}.`];
    }
    return ['Import complete', `The ${nouns} have been imported into ${studioName}.`];
  };
  const headings = {
    2: ['Map Columns', 'Match your Excel columns to InteriorFolio fields.'],
    3: [`Review ${Nouns}`, 'Check imported details and make corrections before saving.'],
    4: step === 4 ? resultHeading() : ['', ''],
  };
  const [title, subtitle] = headings[step];
  const sellPriceCol = mapping.indexOf('sellingPrice');
  const priceCol = mapping.indexOf('price');
  const markupCol = mapping.indexOf('markup');
  const sellPriceHeader =
    sellMode === 'tradeMarkup' ? sheet?.headers[priceCol] : sheet?.headers[sellPriceCol];

  return (
    <div className="pi-page">
      <ImportTarget
        account={account}
        onChangeStudio={changeStudio}
        disabled={Boolean(importing) || undoing}
      />
      <ImportHeader title={title} subtitle={subtitle} />
      <ImportStepper
        labels={['Upload file', 'Map Columns', `Review ${Nouns}`, 'Import']}
        step={step}
      />

      <div className={`pi-body pi-body--step${step}`}>
        {step === 2 && sheet && (
          <ImportMapStep
            itemType={itemType}
            fileName={fileName}
            headers={sheet.headers}
            dataRows={sheet.dataRows}
            mapping={mapping}
            fields={fields}
            onChangeField={(col, field) => {
              dirtyRef.current = true;
              setMapping((prev) => assignColumnField(prev, col, field));
            }}
            sellPriceMode={sellMode}
            onChangeSellPriceMode={(mode) => {
              dirtyRef.current = true;
              setSellPriceMode(mode);
            }}
            onBack={onBackToUpload}
            onContinue={continueToReview}
          />
        )}

        {step === 3 && (
          <ImportReviewStep
            itemType={itemType}
            fields={fields}
            fileName={fileName}
            rows={rows}
            rowStats={rowStats}
            onUpdateRow={updateRow}
            onSelectRows={selectRows}
            onApplyToRows={applyToRows}
            onFillTradePrices={fillTradePrices}
            categoryChoices={categoryChoices}
            vendors={vendors}
            salesCodeOptions={salesCodeOptions}
            sellPrice={{
              mapped: sellPriceModes(mapping).length > 0,
              mode: sellMode,
              header: sellPriceHeader || '',
              markupHeader: markupCol !== -1 ? sheet?.headers[markupCol] : '',
            }}
            uploadingRowIds={uploadingRowIds}
            onUploadRowImage={uploadRowImage}
            onUploadMatchingImages={uploadMatchingImages}
            onAddVendor={handleAddVendor}
            onChangeMapping={() => setStep(2)}
            onBack={() => setStep(2)}
            onSaveDraft={saveDraft}
            onImport={runImport}
            importing={importing}
          />
        )}

        {step === 4 && (
          <ImportResultStep
            itemType={itemType}
            fileName={fileName}
            studioName={studioName}
            results={results}
            attachingIds={attachingIds}
            onAttachImage={attachResultImage}
            onRetryFailed={retryFailed}
            retrying={importing}
            onUndo={() => setConfirmUndo(true)}
            undoing={undoing}
            onDownloadReport={downloadReport}
            onImportAnother={onImportAnother}
            onImportNextSheet={onImportNextSheet}
            nextSheetLabel={nextSheetLabel}
            onChangeStudio={onChangeAccount}
          />
        )}
      </div>

      <ImportProgress progress={progress} />

      {/* Portaled so they stack above the open mobile sidebar. */}
      {createPortal(
        <>
          <ConfirmModal
            isOpen={unsavedGuard.isPrompting}
            title="Leave this import?"
            message={
              importing || undoing
                ? `${Nouns} are still being saved or deleted. Leaving now stops partway through.`
                : 'Your column choices and edits will be lost. Use Save draft on the review step to finish later.'
            }
            confirmLabel="Leave Import"
            destructive={true}
            onConfirm={unsavedGuard.confirmLeave}
            onCancel={unsavedGuard.cancelLeave}
          />
          <ConfirmModal
            isOpen={confirmSwitch}
            title="Switch studios?"
            message={`Your column choices and edits for ${studioName} will be lost. Use Save draft on the review step to finish later.`}
            confirmLabel="Switch studio"
            destructive={true}
            onConfirm={() => {
              setConfirmSwitch(false);
              onChangeAccount();
            }}
            onCancel={() => setConfirmSwitch(false)}
          />
          <ConfirmModal
            isOpen={confirmUndo}
            title="Undo this import?"
            message={`This deletes the ${importedCount} ${
              importedCount === 1 ? noun : nouns
            } this import created in ${studioName}. Vendors and categories it added stay.`}
            confirmLabel={`Delete ${importedCount} ${importedCount === 1 ? noun : nouns}`}
            destructive={true}
            onConfirm={undoImport}
            onCancel={() => setConfirmUndo(false)}
          />
        </>,
        document.body
      )}
    </div>
  );
}
